import { css } from '@linaria/atomic';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { AiContainedIcon } from '@frontend/styled-icons';
import { theme } from '@frontend/theme';
import { NakedButton, Text, Button } from '@frontend/design-system';
import { ConversationTranscript, type ConversationTurn, type ScheduleEventTurn } from './conversation-transcript';

const emptyTranscriptStateTextStyle4 = css`
  grid-row-end: second-end;
  grid-row-start: second-start;
`;

const emptyTranscriptStateTextStyle5 = css`
  grid-row-end: third-end;
  grid-row-start: third-start;
`;

const voiceInterfaceViewDivStyle6 = css`
  width: 100%;
  height: 1px;
  background: var(--fabric-color-border-subtle);
`;

const voiceInterfaceViewTextStyle7 = css`
  margin-bottom: ${theme.spacing(2)};
`;

const voiceInterfaceViewTextStyle8 = css`
  margin-bottom: ${theme.spacing(1)};
  font-size: 12px;
  color: ${theme.colorsV2.neutral60()};
`;

export interface VoiceInterfaceViewState {
  transcripts: ConversationTurn[];
  hasStartedCall: boolean;
  isAISpeaking: boolean;
  isUserSpeaking: boolean;
  showYourTurn: boolean;
  callEnded: boolean;
}

export interface VoiceInterfaceViewProps {
  state: VoiceInterfaceViewState;
  isRecording: boolean;
  onMicButtonPress: () => void;
  onStartNewConversation?: () => void;
  disabled?: boolean;
  settingsChangedBanner?: string | null;
  onViewCalendar?: (turn: ScheduleEventTurn) => void;
  onViewAppointmentRequest?: (requestId: string) => void;
  debugMode?: boolean;
  audioLevel?: number;
  silenceThreshold?: number;
  showScheduleTextConfirmation?: boolean;
}

const containerStyle = css`
  display: grid;
  grid-template-rows: 1fr auto auto;
  height: 100%;
  border-width: 1px;
  border-style: solid;
  border-color: var(--fabric-color-border-subtle);
  border-radius: var(--fabric-radius-md);
  background: var(--fabric-color-surface-canvas);
`;

const micContainerStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing(2)};
  padding-top: ${theme.spacing(4)};
  padding-right: ${theme.spacing(4)};
  padding-bottom: ${theme.spacing(4)};
  padding-left: ${theme.spacing(4)};
  height: 100%;
  width: 100%;
`;

const micButtonStyleClass = css`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  background: var(--mic-button-style-expr0);
  border-width: 4px;
  border-style: solid;
  border-color: var(--mic-button-style-expr1);
  cursor: pointer;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const EmptyTranscriptState = () => {
  const { t } = useTranslation('ai-receptionist');

  return (
    <div
      style={{
        textAlign: 'center',
        padding: theme.spacing(4),
        display: 'grid',
        gridTemplateRows:
          '[first-start] auto [first-end] 16px [second-start] auto [second-end] 8px [third-start] auto [third-end]',
        justifyItems: 'center',
        alignItems: 'center',
        alignContent: 'center',
      }}
    >
      <div style={{ gridRow: 'first-start / first-end' }}>
        <AiContainedIcon iconName='aiweave-assistant' size={124} />
      </div>
      <Text className={emptyTranscriptStateTextStyle4} weight='bold'>
        {t('Click the call button to start a simulated call with the AI Receptionist')}
      </Text>
      <Text className={emptyTranscriptStateTextStyle5}>
        {t(
          "You'll hear how it would respond to patients using the information you've saved in the AI Receptionist's settings."
        )}
      </Text>
    </div>
  );
};

export const VoiceInterfaceView = ({
  state,
  isRecording,
  onMicButtonPress,
  onStartNewConversation,
  disabled = false,
  settingsChangedBanner,
  onViewCalendar,
  onViewAppointmentRequest,
  debugMode = false,
  audioLevel = 0,
  silenceThreshold = 5,
  showScheduleTextConfirmation = false,
}: VoiceInterfaceViewProps) => {
  const { t } = useTranslation('ai-receptionist');
  return (
    <div className={containerStyle}>
      {!state.hasStartedCall ? (
        <EmptyTranscriptState />
      ) : (
        <ConversationTranscript
          transcripts={state.transcripts}
          isAISpeaking={state.isAISpeaking}
          isUserSpeaking={state.isUserSpeaking}
          showYourTurn={state.showYourTurn}
          callEnded={state.callEnded}
          settingsChangedBanner={settingsChangedBanner}
          onViewCalendar={onViewCalendar}
          onViewAppointmentRequest={onViewAppointmentRequest}
          showScheduleTextConfirmation={showScheduleTextConfirmation}
        />
      )}
      <div className={voiceInterfaceViewDivStyle6} />
      <div className={micContainerStyle}>
        {isRecording && debugMode && (
          <>
            <Text weight='bold' className={voiceInterfaceViewTextStyle7}>
              {state.isAISpeaking ? 'AI Speaking...' : state.isUserSpeaking ? 'You are speaking...' : 'Listening...'}
            </Text>
            <Text className={voiceInterfaceViewTextStyle8}>
              Audio Level: {audioLevel.toFixed(2)} | Threshold: {silenceThreshold}
            </Text>
          </>
        )}
        <NakedButton
          className={micButtonStyleClass}
          style={
            {
              '--mic-button-style-expr0': isRecording ? theme.colors.critical50 : theme.colorsV2.green50(),
              '--mic-button-style-expr1': isRecording ? theme.colors.critical70 : theme.colorsV2.green70(),
            } as any
          }
          onClick={onMicButtonPress}
          disabled={disabled}
          aria-label={isRecording ? 'Stop recording' : 'Start recording'}
        >
          {isRecording ? (
            <Icon name='phone-hangup' size={40} color='white' />
          ) : (
            <Icon name='phone' size={40} color='white' />
          )}
        </NakedButton>
        {state.hasStartedCall && onStartNewConversation && (
          <Button variant='secondary' iconName='refresh' onClick={onStartNewConversation}>
            {t('Start New Conversation')}
          </Button>
        )}
      </div>
    </div>
  );
};
